import { resolveErc20MetaToken } from 'utils/resolveErc20MetaToken'
import { STOCKS } from 'utils/stocks/catalogue'
import { ETH_TOKEN, HUB_TOKENS, NATIVE_SENTINEL, WETH } from './addresses'
import type { SwapToken } from './types'

/**
 * The tokenized equities, as swap tokens.
 *
 * They are in the list rather than only on their own pages because a person who
 * has read about AAPLc arrives at the swap wanting to type "AAPL", and an
 * aggregator that makes them paste an address for a token it has a whole page
 * about is being obtuse. Address, symbol and decimals come from the catalogue,
 * so nothing is fetched to show them (scope 40 D2).
 */
export function stockTokens(): SwapToken[] {
  return STOCKS.map((stock) => ({
    address: stock.address,
    symbol: stock.symbol,
    decimals: stock.decimals,
    logoURI: stock.icon,
    isNative: false,
    isMpro: false
  }))
}

/**
 * The token list the page opens with: ETH, the hubs, MPRO if the platform has
 * an address configured for it on Base, and the tokenized stocks. Nothing is
 * fetched — a swap page that cannot list a token until a backend answers is a
 * swap page that depends on a backend, which scope 13 §0 forbids.
 */
export function defaultTokenList(mproAddress: string | null): SwapToken[] {
  const list: SwapToken[] = [ETH_TOKEN, ...HUB_TOKENS]
  if (mproAddress) {
    list.push({
      address: mproAddress.toLowerCase(),
      symbol: 'MPRO',
      decimals: 18,
      isNative: false,
      isMpro: true
    })
  }
  list.push(...stockTokens())
  return list
}

export function isNativeAddress(address: string): boolean {
  return address.toLowerCase() === NATIVE_SENTINEL
}

/**
 * ETH has no pools; WETH does. Everything from discovery onward works on the
 * chain token, and only the execution adapters care that the user meant ETH.
 */
export function toChainToken(token: SwapToken): SwapToken {
  if (!token.isNative) return token
  return { address: WETH, symbol: 'WETH', decimals: 18, isNative: false, isMpro: false }
}

/**
 * Reads an arbitrary address the user pasted. `resolveErc20MetaToken` never
 * throws — it answers `???` / 18 decimals for something that is not a token —
 * so the caller checks the symbol rather than a rejected promise.
 */
export async function resolveToken(
  address: string,
  chainId: number,
  mproAddress: string | null
): Promise<SwapToken> {
  const meta = await resolveErc20MetaToken(address, chainId)
  const addr = address.toLowerCase()
  return {
    address: addr,
    symbol: meta.token,
    decimals: meta.decimals,
    logoURI: meta.logoURI || undefined,
    isNative: false,
    isMpro: mproAddress != null && addr === mproAddress.toLowerCase()
  }
}

/** Whether a resolved token actually answered as an ERC-20. */
export function isResolved(token: SwapToken): boolean {
  return token.symbol !== '???'
}

/** Two tokens are the same for routing purposes once ETH has become WETH. */
export function sameChainToken(a: SwapToken, b: SwapToken): boolean {
  return toChainToken(a).address.toLowerCase() === toChainToken(b).address.toLowerCase()
}
